import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isSafeUrl } from '../../utils/safeUrl';
import {
  Button,
  Space,
  Spin,
  Empty,
  Tooltip,
  InputNumber,
  Typography,
  message,
} from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

// ==================== Styled Components ====================

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height || '600px'};
  width: 100%;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  position: relative;

  ${({ $fullscreen }) =>
    $fullscreen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    border-radius: 0;
    border: none;
  `}
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 768px) {
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.xs};
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ZoomDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-width: 80px;
  justify-content: center;
`;

const PageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ViewerContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  background: ${({ theme }) => theme.colors.tertiary};
  position: relative;
`;

const PDFFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  transform-origin: top center;
  transform: scale(${({ $zoom }) => $zoom / 100});
  transition: transform 0.2s ease;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 10;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  gap: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StyledButton = styled(Button)`
  &.ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ==================== Constants ====================

const ZOOM_LEVELS = [25, 50, 75, 100, 125, 150, 200, 300];
const DEFAULT_ZOOM = 100;
const MIN_ZOOM = 25;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;

// ==================== Utility Functions ====================

/**
 * Convert base64 PDF data to a Blob URL
 * @param {string} base64Data - Base64 encoded PDF data (with or without data URI prefix)
 * @returns {string|null} Blob URL or null if conversion fails
 */
const base64ToBlobUrl = (base64Data) => {
  try {
    // Remove data URI prefix if present
    let cleanBase64 = base64Data;
    if (base64Data.startsWith('data:')) {
      cleanBase64 = base64Data.split(',')[1];
    }

    // Decode base64 to binary
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob and URL
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to convert base64 to blob URL:', error);
    return null;
  }
};

/**
 * Check if a string is a URL safe to load in the iframe / window.open —
 * http(s) or blob only, never javascript: or data: (#275).
 * @param {string} str - String to check
 * @returns {boolean} True if valid URL with an allowed scheme
 */
const isValidUrl = (str) => isSafeUrl(str, ['http:', 'https:', 'blob:']);

/**
 * Check if a string is base64 encoded data
 * @param {string} str - String to check
 * @returns {boolean} True if appears to be base64
 */
const isBase64 = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Check for data URI prefix or pure base64 pattern
  if (str.startsWith('data:application/pdf;base64,')) return true;
  // Check if it looks like base64 (only valid base64 chars and padding)
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  return base64Regex.test(str.replace(/\s/g, ''));
};

// ==================== PDFViewer Component ====================

/**
 * PDFViewer - A reusable PDF preview component with zoom and navigation controls
 *
 * Features:
 * - Accepts base64 PDF data or URL
 * - Zoom in/out controls with preset levels
 * - Fullscreen mode
 * - Download and print functionality
 * - Responsive design
 * - Loading and error states
 *
 * @example
 * // With URL
 * <PDFViewer url="https://example.com/document.pdf" />
 *
 * @example
 * // With base64 data
 * <PDFViewer base64Data="JVBERi0xLjQK..." fileName="report.pdf" />
 *
 * @example
 * // With custom height and controls
 * <PDFViewer
 *   url={pdfUrl}
 *   height="800px"
 *   showDownload
 *   showPrint
 *   onLoad={() => console.log('PDF loaded')}
 * />
 */
const PDFViewer = ({
  // Data sources (provide one)
  url = null,
  base64Data = null,

  // Display options
  height = '600px',
  fileName = 'document.pdf',
  title = null,

  // Feature toggles
  showZoomControls = true,
  showDownload = true,
  showPrint = true,
  showFullscreen = true,
  showPageInfo = true,

  // Initial state
  initialZoom = DEFAULT_ZOOM,

  // Event handlers
  onLoad = null,
  onError = null,
  onDownload = null,

  // Styling
  className = '',
}) => {
  // ==================== State ====================
  const [zoom, setZoom] = useState(initialZoom);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);

  // ==================== Memoized Values ====================

  // Determine the effective PDF URL
  const pdfUrl = useMemo(() => {
    if (url && isValidUrl(url)) {
      return url;
    }
    if (base64Data && isBase64(base64Data)) {
      return blobUrl;
    }
    return null;
  }, [url, base64Data, blobUrl]);

  // Check if we have valid PDF source
  const hasValidSource = useMemo(() => {
    return Boolean(url || base64Data);
  }, [url, base64Data]);

  // ==================== Effects ====================

  // Convert base64 to blob URL when base64Data changes
  useEffect(() => {
    if (base64Data && isBase64(base64Data)) {
      const newBlobUrl = base64ToBlobUrl(base64Data);
      if (newBlobUrl) {
        setBlobUrl(newBlobUrl);
        setError(null);
      } else {
        setError(new Error('Failed to process PDF data'));
      }
    }

    // Cleanup blob URL on unmount or when base64Data changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [base64Data]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullscreen) return;

      switch (e.key) {
        case 'Escape':
          handleExitFullscreen();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          handleResetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, zoom]);

  // ==================== Handlers ====================

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
  }, []);

  const handleZoomChange = useCallback((value) => {
    if (value && value >= MIN_ZOOM && value <= MAX_ZOOM) {
      setZoom(value);
    }
  }, []);

  const handleFitWidth = useCallback(() => {
    // Calculate zoom to fit container width
    setZoom(100);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const handleExitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setError(null);
    onLoad?.();
  }, [onLoad]);

  const handleIframeError = useCallback(
    (e) => {
      setIsLoading(false);
      const err = new Error('Failed to load PDF');
      setError(err);
      onError?.(err);
    },
    [onError]
  );

  const handleDownload = useCallback(() => {
    try {
      const downloadUrl = pdfUrl;
      if (!downloadUrl) {
        message.error('No PDF available to download');
        return;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onDownload?.();
      message.success('Download started');
    } catch (err) {
      message.error('Failed to download PDF');
      console.error('Download error:', err);
    }
  }, [pdfUrl, fileName, onDownload]);

  const handlePrint = useCallback(() => {
    try {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      } else {
        message.warning('Please allow popups to print');
      }
    } catch (err) {
      message.error('Failed to print PDF');
      console.error('Print error:', err);
    }
  }, [pdfUrl]);

  const handleReload = useCallback(() => {
    setIsLoading(true);
    setError(null);
    // Force iframe reload by temporarily clearing the URL
    const iframe = document.querySelector('#pdf-viewer-iframe');
    if (iframe) {
      const currentSrc = iframe.src;
      iframe.src = '';
      setTimeout(() => {
        iframe.src = currentSrc;
      }, 100);
    }
  }, []);

  const handlePageChange = useCallback((page) => {
    if (page >= 1 && (!totalPages || page <= totalPages)) {
      setCurrentPage(page);
      // Note: iframe navigation to specific pages is limited
      // Full page navigation would require react-pdf or similar library
    }
  }, [totalPages]);

  // ==================== Render Functions ====================

  const renderToolbar = () => (
    <Toolbar>
      {/* Left section - File info */}
      <ToolbarSection>
        {title && (
          <FileInfo>
            <FilePdfOutlined style={{ color: 'var(--odin-error)' }} />
            <Text ellipsis style={{ color: 'inherit' }}>
              {title || fileName}
            </Text>
          </FileInfo>
        )}
      </ToolbarSection>

      {/* Center section - Zoom controls */}
      {showZoomControls && (
        <ToolbarSection>
          <Tooltip title="Zoom out (-)">
            <StyledButton
              type="text"
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={zoom <= MIN_ZOOM}
              size="small"
            />
          </Tooltip>

          <ZoomDisplay>
            <InputNumber
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              value={zoom}
              onChange={handleZoomChange}
              size="small"
              style={{ width: 60 }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
              bordered={false}
            />
          </ZoomDisplay>

          <Tooltip title="Zoom in (+)">
            <StyledButton
              type="text"
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoom >= MAX_ZOOM}
              size="small"
            />
          </Tooltip>

          <Tooltip title="Fit to width">
            <StyledButton
              type="text"
              icon={<ExpandOutlined />}
              onClick={handleFitWidth}
              size="small"
            />
          </Tooltip>
        </ToolbarSection>
      )}

      {/* Right section - Actions */}
      <ToolbarSection>
        {showPageInfo && totalPages && (
          <PageInfo>
            <Button
              type="text"
              icon={<LeftOutlined />}
              size="small"
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
            />
            <span>
              {currentPage} / {totalPages}
            </span>
            <Button
              type="text"
              icon={<RightOutlined />}
              size="small"
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            />
          </PageInfo>
        )}

        <Space size="small">
          {error && (
            <Tooltip title="Reload">
              <StyledButton
                type="text"
                icon={<ReloadOutlined />}
                onClick={handleReload}
                size="small"
              />
            </Tooltip>
          )}

          {showDownload && (
            <Tooltip title="Download PDF">
              <StyledButton
                type="text"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={!pdfUrl}
                size="small"
              />
            </Tooltip>
          )}

          {showPrint && (
            <Tooltip title="Print">
              <StyledButton
                type="text"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={!pdfUrl}
                size="small"
              />
            </Tooltip>
          )}

          {showFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
              <StyledButton
                type="text"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={handleToggleFullscreen}
                size="small"
              />
            </Tooltip>
          )}
        </Space>
      </ToolbarSection>
    </Toolbar>
  );

  const renderContent = () => {
    // No source provided
    if (!hasValidSource) {
      return (
        <ErrorContainer>
          <FilePdfOutlined style={{ fontSize: 48, opacity: 0.5 }} />
          <Empty
            description="No PDF document provided"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </ErrorContainer>
      );
    }

    // Error state
    if (error) {
      return (
        <ErrorContainer>
          <FilePdfOutlined style={{ fontSize: 48, color: 'var(--odin-error)' }} />
          <Text type="danger">Failed to load PDF</Text>
          <Text type="secondary">{error.message}</Text>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleReload}>
            Retry
          </Button>
        </ErrorContainer>
      );
    }

    // PDF URL not ready yet (still processing base64)
    if (!pdfUrl) {
      return (
        <LoadingOverlay>
          <Spin size="large" />
          <Text type="secondary">Processing PDF...</Text>
        </LoadingOverlay>
      );
    }

    return (
      <>
        {isLoading && (
          <LoadingOverlay>
            <Spin size="large" />
            <Text type="secondary">Loading PDF...</Text>
          </LoadingOverlay>
        )}
        <PDFFrame
          id="pdf-viewer-iframe"
          src={pdfUrl}
          $zoom={zoom}
          title={title || fileName}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </>
    );
  };

  // ==================== Main Render ====================

  return (
    <ViewerContainer
      $height={height}
      $fullscreen={isFullscreen}
      className={className}
    >
      {renderToolbar()}
      <ViewerContent>{renderContent()}</ViewerContent>
    </ViewerContainer>
  );
};

// ==================== PropTypes ====================

PDFViewer.propTypes = {
  /** URL to the PDF document */
  url: PropTypes.string,
  /** Base64 encoded PDF data (with or without data URI prefix) */
  base64Data: PropTypes.string,
  /** Height of the viewer container */
  height: PropTypes.string,
  /** Filename for download */
  fileName: PropTypes.string,
  /** Title displayed in the toolbar */
  title: PropTypes.string,
  /** Show zoom controls */
  showZoomControls: PropTypes.bool,
  /** Show download button */
  showDownload: PropTypes.bool,
  /** Show print button */
  showPrint: PropTypes.bool,
  /** Show fullscreen button */
  showFullscreen: PropTypes.bool,
  /** Show page navigation info */
  showPageInfo: PropTypes.bool,
  /** Initial zoom level (25-300) */
  initialZoom: PropTypes.number,
  /** Callback when PDF loads successfully */
  onLoad: PropTypes.func,
  /** Callback when PDF fails to load */
  onError: PropTypes.func,
  /** Callback when download is triggered */
  onDownload: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default PDFViewer;
